import React, { useState, useEffect,  useRef } from 'react';
import { FileText, Clock, CheckCircle, AlertTriangle, Building, Calendar } from 'lucide-react';
import { departments, monthNames } from './constants';
import { getStatusColor, calculateDuration, formatDate, getDepartmentStats, getUpcomingLeaves, calculateBusinessDays} from './helpers';
import { getSickPendingLeaves, getApprovedLeaves, getAllLeaves, getSickLeaves, approveLeave, rejectLeave, getLeavesPerDepartment } from '../../services/leavesAPIs';
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';



const Overview = ({ leavesData, filters, setFilters, setLeavesData }) => {
  const alertRef = useRef(null);
  const [ allLeaves, setAllLeaves ] = useState([]);
  const [criticalRequests, setCriticalRequests] = useState([]);
  const [deptLeaves, setDeptLeaves] = useState({});
  const [approvedLeaves, setApprovedLeaves] = useState([]);

  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  

  const [successMessage, setSuccessMessage] = useState('');
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');


  const leaveTypeLabels = {
    SICK_LEAVE: 'Sick Leave',
    ANNUAL_LEAVE: 'Annual Leave',
    UNPAID_LEAVE: 'Unpaid Leave',
    MATERNITY_LEAVE: 'Maternity Leave',
  };

  const getApiErrorMessage = (error, fallbackMessage) => {
    const responseData = error?.response?.data;
    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData;
    }
    if (responseData?.message) {
      return responseData.message;
    }
    if (responseData?.error) {
      return responseData.error;
    }
    return error?.message || fallbackMessage;
  };


  const fetchData = async () => {
  try {
    const allLeavesData = await getAllLeaves();
    console.log("All Overview Leaves Data:", allLeavesData);
    setAllLeaves(allLeavesData);
    setLeavesData(allLeavesData);

    const approvedLeavesData = await getApprovedLeaves();
    setApprovedLeaves(approvedLeavesData);

    const departmentLeavesData = await getLeavesPerDepartment();
    setDeptLeaves(departmentLeavesData);

    const sickPending= await getSickPendingLeaves();
    setCriticalRequests(sickPending);
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

useEffect(() => {
  fetchData();
}, []);

  const handleOpenRejectDialog = (leaveId) => {
    setSelectedLeaveId(leaveId);
    setRejectionReason('');
    setOpenRejectDialog(true);
  };

  const handleCloseRejectDialog = () => {
    setOpenRejectDialog(false);
    setSelectedLeaveId(null);
    setRejectionReason('');
  };

  const handleRejectSubmit = () => {
    if (rejectionReason.trim()) {
      handleStatusChange(selectedLeaveId, 'rejected', rejectionReason);
      handleCloseRejectDialog();
    } else {
      setErrorMessage('Please provide a rejection reason.');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };



  // Filter upcoming leaves to show only those starting from today up to 4 weeks from now
  const filterUpcomingLeaves = (leaves) => {
  const now = new Date();
  const fourWeeksLater = new Date();
  fourWeeksLater.setDate(now.getDate() + 28); // 28 days = 4 weeks

  return leaves.filter(leave => {
    const startDate = new Date(leave.startDate);
    // Include leaves starting from today up to 4 weeks from now
    return startDate >= now && startDate <= fourWeeksLater;
    });
  };


  const updateOverviewCards = () => {
  const typeCounts = {
    vacation: 0,
    personal: 0,
    sick: 0,
  };

  const statusCounts = {
    pending: 0,
    approved: 0,
    rejected: 0,
  };

  allLeaves.forEach(leave => {
    const typeKey = leave.type.toLowerCase();
    const statusKey = leave.status.toLowerCase();

    if (typeCounts.hasOwnProperty(typeKey)) {
      typeCounts[typeKey]++;
    } else {
      console.warn(`Unknown leave type: ${typeKey}`);
    }

    if (statusCounts.hasOwnProperty(statusKey)) {
      statusCounts[statusKey]++;
    } else {
      console.warn(`Unknown leave status: ${statusKey}`);
    }
  });

  return {
    ...typeCounts,
    ...statusCounts,
    total: allLeaves.length,
  };
};


   const handleStatusChange = async (leaveId, newStatus, reason = '') => {
        try {
          let updatedLeave;
          if (newStatus === 'approved') {
            updatedLeave = await approveLeave(leaveId);
          } else if (newStatus === 'rejected') {
            updatedLeave = await rejectLeave(leaveId, { reason });
            setSuccessMessage(`Leave rejected successfully. Reason: ${reason}`);
          }

      setSuccessMessage(`Leave ${newStatus.toLowerCase()} successfully.`);
      setErrorMessage('');
      if (alertRef.current) {
          alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      // Clear message after a few seconds
      setTimeout(() => setSuccessMessage(''), 8000);

      // ✅ Refetch after action
      await fetchData();

    } catch (error) {
      console.error(`Failed to ${newStatus.toLowerCase()} leave:`, error);
      const fallbackMessage = `Failed to ${newStatus.toLowerCase()} leave. Please try again.`;
      const errorMsg = getApiErrorMessage(error, fallbackMessage);
      setErrorMessage(errorMsg.includes('contract')
        ? 'Cannot approve: Employee has no contract. Please add one.'
        : errorMsg
      );
      setSuccessMessage('');
      if (alertRef.current) {
          alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      setTimeout(() => setErrorMessage(''), 4000);
    }
  };

  const counts = updateOverviewCards();
  const deptStats = getDepartmentStats(leavesData, filters.month);
  //const upcomingLeaves = getUpcomingLeaves(leavesData);
  const upcomingLeaves = filterUpcomingLeaves(approvedLeaves);


  return (
    <div className="Leaves-overview-container">
      <OverviewMetrics counts={counts} criticalRequests={criticalRequests} />
      <div ref={alertRef}>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 800 }}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, maxWidth: 800 }}>
          {successMessage}
        </Alert>
      )}
      </div>
      <CriticalRequests 
        criticalRequests={criticalRequests}
        handleStatusChange={handleStatusChange}
        formatDate={formatDate}
        leaveTypeLabels={leaveTypeLabels}
        handleOpenRejectDialog={handleOpenRejectDialog}
      />
      <div className="Leaves-stats-grid">
        <DepartmentStats 
          deptStats={deptLeaves}
          filters={filters}
          setFilters={setFilters}
          monthNames={monthNames}
        />
        <UpcomingLeaves 
          upcomingLeaves={upcomingLeaves}
          formatDate={formatDate}
          calculateDuration={calculateBusinessDays}
          leaveTypeLabels={leaveTypeLabels}
        />
        <Dialog open={openRejectDialog} onClose={handleCloseRejectDialog}>
          <DialogTitle>Reject Leave Request</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Reason for Rejection"
                type="text"
                fullWidth
                variant="outlined"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRejectDialog}>Cancel</Button>
            <Button onClick={handleRejectSubmit} color="error">Reject</Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};
const CriticalRequests = ({ criticalRequests, handleStatusChange, formatDate, leaveTypeLabels, handleOpenRejectDialog }) => (
  criticalRequests.length > 0 && (
    <div className="Leaves-critical-alert">
      <div className="Leaves-critical-header">
        <AlertTriangle className="Leaves-critical-icon" />
        <h3 className="Leaves-critical-title">Critical Requests Requiring Attention</h3>
      </div>
      <div className="Leaves-critical-list">
        {criticalRequests.map(leave => (
          <div key={leave.requestId} className="Leaves-critical-item">
            <div className="Leaves-critical-details">
              <p className="Leaves-critical-name">{leave.employee?.firstName } {leave.employee?.lastName} - {leaveTypeLabels[leave.type] || leave.type}</p>
              <p className="Leaves-critical-dates">{formatDate(leave.startDate)} to {formatDate(leave.endDate)}</p>
            </div>
            {leave.status =='pending' &&
            <div className="Leaves-action-buttons">
              <button 
                onClick={() => handleStatusChange(leave.requestId, 'approved')}
                className="Leaves-btn Leaves-btn-save"
              >
                ✓
              </button>
              <div style={{width:'20px'}}></div>
              {/* <button 
                onClick={() => handleStatusChange(leave.requestId, 'rejected')}
                className="Leaves-btn Leaves-btn-delete"
              >
                ✗
              </button> */}
              <button 
                onClick={() => handleOpenRejectDialog(leave.requestId)}
                className="Leaves-btn Leaves-btn-delete"
              >
                ✗
              </button>
              <div style={{width:'20px'}}></div>
            </div>
            }
          </div>
        ))}
      </div>
    </div>
  )
);

const OverviewMetrics = ({ counts, criticalRequests }) => (
  <div className="Leaves-metrics-grid">
    {/* Total Requests */}
    <div className="Leaves-metric-card Leaves-metric-total">
      <div className="Leaves-metric-content">
        <p className="Leaves-metric-label">Total Requests</p>
        <p className="Leaves-metric-value">{counts.total}</p>
      </div>
      <FileText className="Leaves-metric-icon" />
    </div>

    {/* Pending Approval */}
    <div className="Leaves-metric-card Leaves-metric-pending">
      <div className="Leaves-metric-content">
        <p className="Leaves-metric-label">Pending Approval</p>
        <p className="Leaves-metric-value">{counts.pending}</p>
      </div>
      <Clock className="Leaves-metric-icon" />
    </div>

    {/* Approved */}
    <div className="Leaves-metric-card Leaves-metric-approved">
      <div className="Leaves-metric-content">
        <p className="Leaves-metric-label">Approved</p>
        <p className="Leaves-metric-value">{counts.approved}</p>
      </div>
      <CheckCircle className="Leaves-metric-icon" />
    </div>

    {/* Critical Requests */}
    <div className="Leaves-metric-card Leaves-metric-critical">
      <div className="Leaves-metric-content">
        <p className="Leaves-metric-label">Critical Requests</p>
        <p className="Leaves-metric-value">{criticalRequests.length}</p>
      </div>
      <AlertTriangle className="Leaves-metric-icon" />
    </div>
  </div>
);


const DepartmentStats = ({ deptStats, filters, setFilters, monthNames }) => (
  <div className="Leaves-stats-card">
    <div className="Leaves-stats-header">
      <h3 className="Leaves-stats-title">
        <Building className="Leaves-stats-icon" />
        Department Statistics
      </h3>
      <select 
        value={filters.month} 
        onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value ? parseInt(e.target.value) : null }))}
        className="Leaves-filter-select"
      >
        <option value="">All Months</option>
        {monthNames.map((month, index) => (
          <option key={index} value={index + 1}>{month}</option>
        ))}
      </select>
    </div>
    <div className="Leaves-stats-list">
      {Object.entries(deptStats).map(([dept, stats]) => (
        <div key={dept} className="Leaves-stats-item">
          <span className="Leaves-stats-dept">{dept}</span>
          <div className="Leaves-stats-values">
            <span className="Leaves-stats-total">Total: {stats.total}</span>
            <span className="Leaves-stats-pending">Pending: {stats.pending}</span>
            <span className="Leaves-stats-approved">Approved: {stats.approved}</span>
            <span className="Leaves-stats-rejected">Rejected: {stats.rejected}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const UpcomingLeaves = ({ upcomingLeaves, formatDate, calculateDuration, leaveTypeLabels }) => (
  <div className="Leaves-stats-card">
    <h3 className="Leaves-stats-title">
      <Calendar className="Leaves-stats-icon" />
      Upcoming Approved Leaves
    </h3>
    <div className="Leaves-stats-list">
      {upcomingLeaves.map(leave => (
        <div key={leave.id} className="Leaves-stats-item Leaves-stats-upcoming">
          <div className="Leaves-stats-details">
            <p className="Leaves-stats-name">{leave.employee?.firstName} {leave.employee?.lastName}</p>
            <p className="Leaves-stats-info">
              {leave.employee?.department.name} • {leaveTypeLabels[leave.type] || leave.type}
            </p>

          </div>
          <div className="Leaves-stats-dates">
            <p className="Leaves-stats-date">{formatDate(leave.startDate)}</p>
            <p className="Leaves-stats-duration">{calculateDuration(leave.startDate, leave.endDate)} days</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default Overview;
